import { useEffect, useState } from 'preact/hooks';
import { type ApprovalSummary, DashboardApi, HttpError } from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

export function Approvals({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [approvals, setApprovals] = useState<ApprovalSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'approved' | 'revoked'>('approved');

  const refresh = async () => {
    try {
      const r = await api.listApprovals(org.id);
      setApprovals(r.approvals);
      setError(null);
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh stable
  useEffect(() => {
    void refresh();
  }, [org.id]);

  const onRevoke = async (approval: ApprovalSummary) => {
    const reason = prompt(
      `Revoke this approval?\n\nVPAT hash: ${approval.vpatHash.slice(0, 16)}…\nApproved: ${new Date(approval.approvedAt).toLocaleString()}\n\nReason (required):`,
    );
    if (!reason || reason.trim().length === 0) return;
    try {
      await api.revokeApproval(org.id, approval.id, reason.trim());
      await refresh();
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    }
  };

  const filtered = approvals?.filter((a) => filter === 'all' || a.status === filter) ?? [];

  return (
    <>
      <div class="page-header">
        <h1>VPAT approvals</h1>
        <p>
          Every approved VPAT is hashed (SHA-256) and chained into the audit log. Revoking an
          approval preserves the row + hash for audit; the next render produces a new approval.
        </p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      <div class="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Approvals in {org.name}</h2>
          <label class="small muted">
            Filter:{' '}
            <select
              value={filter}
              onChange={(e) => setFilter((e.target as HTMLSelectElement).value as typeof filter)}
            >
              <option value="approved">Active</option>
              <option value="revoked">Revoked</option>
              <option value="all">All</option>
            </select>
          </label>
        </div>

        {approvals === null && (
          <p class="small muted">
            <output>Loading…</output>
          </p>
        )}
        {approvals !== null && filtered.length === 0 && (
          <div class="empty-state" style={{ padding: '24px 8px' }}>
            <p>
              {filter === 'approved'
                ? 'No active approvals yet. Render a VPAT and approve it from the Compliance page.'
                : 'Nothing matches this filter.'}
            </p>
          </div>
        )}
        {filtered.length > 0 && (
          <table class="data-table" style={{ width: '100%', marginTop: 16 }}>
            <thead>
              <tr>
                <th>Status</th>
                <th>VPAT hash</th>
                <th>Edition</th>
                <th>Standards</th>
                <th>Scan</th>
                <th>Approved</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id}>
                  <td>
                    <span
                      class={`pill ${a.status === 'approved' ? 'pill-supports' : 'pill-muted'}`}
                    >
                      {a.status}
                    </span>
                  </td>
                  <td>
                    <code title={a.vpatHash}>{a.vpatHash.slice(0, 12)}…</code>
                  </td>
                  <td>{a.edition}</td>
                  <td class="small">{a.standards.join(' · ')}</td>
                  <td>
                    <code class="small">{a.scanId.slice(0, 10)}…</code>
                  </td>
                  <td class="small muted">{new Date(a.approvedAt).toLocaleString()}</td>
                  <td>
                    {a.status === 'approved' ? (
                      <button type="button" class="link-button" onClick={() => void onRevoke(a)}>
                        Revoke
                      </button>
                    ) : (
                      <span class="small muted" title={a.revocationReason ?? ''}>
                        revoked {a.revokedAt ? new Date(a.revokedAt).toLocaleDateString() : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
